'use client';

/**
 * IntentBriefCard — buyer input + synthesized read of an Intent.
 *
 * First of three rail cards mode3 splits the legacy `intent` Details
 * panel into. Renders sections 1, 3-7 of `ENTITY_SCHEMA["intent"]`:
 * gallery, source filename, room label, designer brief, derived
 * "Room brief" prose, and keyword tags.
 *
 * Composed from rail-native section primitives under
 * `rail-insights/sections/` — independent of the legacy
 * `copilot/details/sections/*` infrastructure that retires with
 * CopilotPanel. Visual fidelity matches the legacy paint by design;
 * the divergence is in *where the primitives live*, not in what they
 * render.
 *
 * Selection-aware: paints only when an Intent is the active viewport
 * selection. Reads the same `intent_details` strategy the
 * `IntentMatchQualityCard` reads; React Query dedups across both.
 *
 * @see klyde_edge/composition/cards/intent.py — content_key="intent_brief"
 * @see klyde_edge/ui_config/entity_schema.py — ENTITY_SCHEMA["intent"]
 *
 * @component_role: Card
 * @component_type: Rail Card
 * @domain: Copilot / Intent
 * @layer: presentation
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardExport } from '@/lib/design/card-export';
import type { IntentListResponse } from '@/lib/api/types';
import { RailCard } from '../rail-card';
import { GallerySection } from '../sections/gallery-section';
import { TextBlock } from '../sections/text-block';
import { TagCloud } from '../sections/tag-cloud';

const INTENT_DETAILS_STRATEGY = 'intent_details';

// Match the field set declared on ENTITY_SCHEMA["intent"] section 7
// (TAGS) so the keyword pill cloud renders the same flattened set the
// legacy details panel does.
const TAG_FIELDS = ['style', 'mood', 'primary_material', 'primary_color'];

export function IntentBriefCard() {
  const { entityType, entityId } = useViewportSelection();
  const enabled = entityType === 'intent' && !!entityId;

  const { data, isLoading, error } = useStrategyData<IntentListResponse>(
    INTENT_DETAILS_STRATEGY,
    enabled ? { intent_sid: entityId ?? '' } : {},
    enabled,
  );

  // Scene export — register what this card paints so the capture
  // envelope mirrors the rail. `query_key` matches the useStrategyData
  // fetch exactly (['strategy-data', strategy, params]) so the payload
  // half resolves from the same React Query cache entry all three intent
  // rail cards share. `status` derives from paintable content, not
  // React Query's isLoading. Called before the early returns per Rules
  // of Hooks (the mode9 cards do the same).
  useCardExport(
    useMemo(() => {
      const it = data?.data?.items?.[0];
      const keywords = it
        ? TAG_FIELDS.flatMap((f) => {
            const v = (it as unknown as Record<string, unknown>)[f];
            return Array.isArray(v) ? (v as unknown[]).map(String) : v ? [String(v)] : [];
          })
        : [];
      const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
        ? 'idle'
        : isLoading
          ? 'loading'
          : error
            ? 'error'
            : it
              ? 'loaded'
              : 'idle';
      const rows = it
        ? [
            it.source_filename ? `Source: ${it.source_filename}` : '',
            it.room_label ? `Space: ${it.room_label}` : '',
            it.designer_brief ? `Designer brief: ${it.designer_brief}` : '',
            it.intent_description ? `Room brief: ${it.intent_description}` : '',
            keywords.length ? `Keywords: ${keywords.join(', ')}` : '',
          ].filter(Boolean)
        : [];
      return {
        label: 'Brief',
        content_key: 'intent_brief',
        surface: 'rail' as const,
        status,
        query_key: enabled
          ? ['strategy-data', INTENT_DETAILS_STRATEGY, { intent_sid: entityId ?? '' }]
          : null,
        text: it ? [`Brief — ${it.name ?? ''}`.trim(), ...rows].join('\n') : 'Brief',
        markdown: it
          ? [`## Brief — ${it.name ?? ''}`.trim(), ...rows.map((r) => `- ${r}`)].join('\n')
          : '## Brief',
        json: it
          ? {
              name: it.name ?? '',
              source: it.source_filename ?? '',
              space: it.room_label ?? '',
              designer_brief: it.designer_brief ?? '',
              room_brief: it.intent_description ?? '',
              keywords,
              media_urls: it.media_urls ?? [],
            }
          : null,
      };
    }, [data, enabled, entityId, isLoading, error]),
  );

  if (!enabled) {
    return (
      <RailCard title="">
        <div className="text-xs text-muted-foreground">Select an Intent to see its brief</div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title="">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading brief…
        </div>
      </RailCard>
    );
  }

  if (error || !data?.data) {
    return (
      <RailCard title="">
        <div className="text-xs text-destructive">
          {error ? `Failed to load: ${(error as Error).message}` : 'No data'}
        </div>
      </RailCard>
    );
  }

  const intent = data.data.items?.[0];
  if (!intent) {
    return (
      <RailCard title="">
        <div className="text-xs text-muted-foreground">No data for this Intent.</div>
      </RailCard>
    );
  }

  // TagCloud takes the raw entity record + the field list and dedupes
  // across them. Cast through unknown for the type-incomplete
  // projection from response → Record.
  const tagData = intent as unknown as Record<string, unknown>;

  return (
    <RailCard title="">
      <div className="space-y-3">
        <GallerySection
          urls={intent.media_urls}
          alt={intent.name ?? 'Intent'}
          aspectRatio="square"
        />
        <TextBlock text={intent.source_filename} label="Source" />
        <TextBlock text={intent.room_label} label="Space" />
        <TextBlock text={intent.designer_brief} label="Designer brief" />
        <TextBlock text={intent.intent_description} label="Room brief" italic />
        <TagCloud data={tagData} fields={TAG_FIELDS} label="Keywords" />
      </div>
    </RailCard>
  );
}
